import { FC, useEffect, useRef, useState } from 'react';
import './ArticlesCommentsInput.scss';
import { useForm } from 'react-hook-form';
import { useArticleComment } from '../../../providers/Articles/ArticlesCommentsProvider';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import ErrorMessage from '../../ErrorMessage/ErrorMessage';
import { useAuth } from '../../../providers/AuthProvider';
import { schema } from './Validate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

interface ArticlesCommentsInputProps {
  articleId?: number;
  comment?: { id?: number; comment?: string } | null;
}

type FormValues = {
  id?: number;
  comment: string;
};

const ArticlesCommentsInput: FC<ArticlesCommentsInputProps> = ({ articleId, comment }) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { handleFetch, handleList } = useArticleComment();
  const { onInvalid } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: { comment: '', id: undefined },
  });

  const onSubmit = async (data: FormValues) => {
    setIsLoadingSubmit(true);
    const ret = await handleFetch({
      article_id: articleId,
      comment: data.comment,
      id: data?.id ?? null,
    });

    if (ret) {
      toast.success('Comentário salvo com sucesso!');
      handleList();
      reset({ comment: '', id: undefined });
    }

    setIsLoadingSubmit(false);
  };

  useEffect(() => {
    if (comment && (comment.comment ?? '').length > 0) {
      reset({ comment: comment.comment ?? '', id: comment.id });
      setTimeout(() => setFocus('comment', { shouldSelect: false }), 0);
      requestAnimationFrame(() => textareaRef.current?.focus());
    } else {
      reset({ comment: '', id: undefined });
    }
  }, [comment, reset, setFocus]);

  const commentReg = register('comment');

  return (
    <div className="ArticlesCommentsInput" data-testid="ArticlesCommentsInput">
      <form onSubmit={handleSubmit(onSubmit)}>
        <textarea
          {...commentReg}
          ref={(el) => {
            commentReg.ref(el);
            textareaRef.current = el;
          }}
          className={`form-control mt-4 ${onInvalid(errors?.comment)}`}
          rows={4}
          placeholder="Escreva seu comentário..."
        />
        <ErrorMessage name={'Comentário'} type={errors?.comment?.type?.toString()} min={3} />

        <div className="d-flex justify-content-end align-items-center mt-2">
          <button type="submit" className="btn btn-primary btn-sm" disabled={isSubmitting || isLoadingSubmit}>
            {!isLoadingSubmit ? 'Enviar' : <FontAwesomeIcon icon={faSpinner} spin />}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ArticlesCommentsInput;
